<template>
  <div class="hero-carousel container mt-lg-0 mt-4" ref="carouselRef">
    <div
      class="carousel-container"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <div v-for="(slide, index) in slides" :key="index" class="carousel-slide">
        <div class="slide-content">
          <!-- Text content -->
          <div class="slide-text">
            <h1 class="slide-title text-gradient-primary">{{ slide.title }}</h1>
            <p class="slide-description col-lg-9">{{ slide.description }}</p>
            <div class="slide-buttons">
              <a :href="slide.primaryLink" class="btn btn-primary">{{
                slide.primaryText
              }}</a>
              <a href="/productos" class="btn btn-outline-light"
                >Ir al catálogo</a
              >
            </div>
          </div>

          <!-- Image content -->
          <div class="slide-image-wrapper">
            <a :href="slide.primaryLink" class="image-link">
              <img :src="slide.image" :alt="slide.title" class="slide-image" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicators -->
    <div class="carousel-indicators">
      <button
        v-for="(_, index) in slides"
        :key="index"
        class="indicator-dot"
        :class="{ active: currentIndex === index }"
        @click="goToSlide(index)"
      ></button>
    </div>

    <!-- Touch area for swipe -->
    <div
      class="touch-area"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const slides = [
  {
    title: "KITS COMPLETOS\nPARA TU INDOOR",
    description:
      "Encuentra todo lo necesario para armar tu cultivo indoor. Carpa, iluminación y ventilación en un solo lugar.",
    image: new URL(
      "@/assets/images/products/combo-indoor-2.png",
      import.meta.url
    ).href,
    primaryLink: "/productos?categories=Artículos indoor",
    primaryText: "Ver combos",
  },
  {
    title: "FERTILIZANTES\nDE PRIMERA",
    description:
      "Nutrientes y fertilizantes de las mejores marcas para tus plantas. ¡Cultiva con calidad y cosecha con sabor!",
    image: new URL(
      "@/assets/images/products/nutripack-namaste.png",
      import.meta.url
    ).href,
    primaryLink: "/productos?categories=Fertilizantes",
    primaryText: "Ver packs",
  },
  {
    title: "SUSTRATOS\nORGÁNICOS",
    description:
      "Sustratos de calidad para tus plantas. ¡Cultiva con calidad y cosecha con sabor!",
    image: new URL("@/assets/images/products/sustratos.png", import.meta.url)
      .href,
    primaryLink: "/productos?categories=sustratos",
    primaryText: "Ver Sustratos",
  },
];

const currentIndex = ref(0);
const touchStartX = ref(0);
const autoplayInterval = ref(null);
const AUTOPLAY_DELAY = 5000;

const goToSlide = (index) => {
  currentIndex.value = index;
  resetAutoplay();
};

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % slides.length;
};

const prevSlide = () => {
  currentIndex.value = (currentIndex.value - 1 + slides.length) % slides.length;
};

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX;
};

const handleTouchMove = (e) => {
  if (!touchStartX.value) return;

  const touchEndX = e.touches[0].clientX;
  const diff = touchStartX.value - touchEndX;

  if (Math.abs(diff) > 50) {
    if (diff > 0) {
      nextSlide();
    } else {
      prevSlide();
    }
    touchStartX.value = null;
  }
};

const handleTouchEnd = () => {
  touchStartX.value = null;
};

const startAutoplay = () => {
  autoplayInterval.value = setInterval(nextSlide, AUTOPLAY_DELAY);
};

const resetAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    startAutoplay();
  }
};

onMounted(() => {
  startAutoplay();
});

onUnmounted(() => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
  }
});
</script>

<style scoped>
.hero-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  height: 70vh;
}

.carousel-container {
  display: flex;
  transition: transform 0.5s ease-out;
  height: 100%;
}

.carousel-slide {
  min-width: 100%;
  height: 100%;
}

.slide-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 2rem 0;
  gap: 2rem;
}

.slide-text {
  flex: 1;
  max-width: 600px;
  opacity: 0;
  transform: translateY(20px);
  animation: slideIn 0.5s forwards;
}

.slide-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  white-space: pre-line;
}

.slide-description {
  font-size: 1.1rem;
  color: var(--white-color-2);
  margin-bottom: 2rem;
  opacity: 0.8;
}

.slide-buttons {
  display: flex;
  gap: 1rem;
  position: relative;
  z-index: 2; /* Asegurar que los botones estén por encima */
}

.slide-image-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.slide-image {
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
  opacity: 0;
  transform: translateY(20px);
  animation: slideIn 0.5s 0.2s forwards;
  filter: brightness(0.9);
  transition: all 0.3s ease;
  margin: 0 auto; /* Centrar horizontalmente */
  display: block; /* Asegurar comportamiento de bloque */
}

.slide-image:hover {
  filter: brightness(1);
}

.carousel-indicators {
  position: absolute;
  bottom: -10px;
  left: 35%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2; /* Asegurar que los indicadores estén por encima */
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  margin-top: 55px !important;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator-dot.active {
  background: var(--white-color);
  transform: scale(1.2);
}

.touch-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none; /* Permitir clicks a través del touch-area */
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.image-link {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.image-link:hover {
  transform: scale(1.02);
}

.image-link:hover .slide-image {
  filter: brightness(1);
}

@media (max-width: 991.98px) {
    .hero-carousel {
        height: auto;
    }
    .carousel-indicators{
        bottom: -1rem;
    }
  .slide-content {
    flex-direction: column;
    text-align: center;
    padding: 1rem;
  }

  .slide-title {
    font-size: 2.5rem;
  }

  .slide-description {
    font-size: 1rem;
    padding: 0 1rem;
  }

  .slide-buttons {
    justify-content: center;
  }

  .slide-image {
    max-height: 300px;
    width: auto;
    margin: 0 auto;
  }
}
</style>
